import { Component, OnInit, OnDestroy, computed, signal, DestroyRef, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CommonModule } from '@angular/common';
import { Dir } from '@angular/cdk/bidi';
import { CacheService } from './_services/cache.service';
import { LoaderComponent } from './components/shared/loader/loader.component';
import { SettingsService } from './_services/settings.service';
import { AnalyticsService } from './_services/analytics.service';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StorageService } from './_services/storage.servive';
import { AuthService } from './_services/auth.service';
import { UploadProgressComponent } from './components/addons/attachments/upload-progress/upload-progress.component';
import { AttachmentQueueService } from './_services/attachment-queue.service';
import { EntityRemovalNotificationProcessorService } from './_services/entity-removal-notification-processor.service';
import { UpdateBannerComponent } from './components/shared/update-banner/update-banner.component';
import { UpdateModalComponent } from './components/shared/update-modal/update-modal.component';
import { LogShipperService } from './_services/logger';
import { HabitTimerSyncService } from './_services/habit-timer-sync.service';
import { AppDownloadBannerComponent } from './components/shared/app-download-banner/app-download-banner.component';
import { RateLimitBannerComponent } from './components/shared/rate-limit-banner/rate-limit-banner.component';
import { ReleaseConfigService } from './_services/release-config.service';

@Component({
    selector: 'app-root',
    imports: [
        CommonModule,
        RouterOutlet,
        Dir,
        LoaderComponent,
        UploadProgressComponent,
        UpdateBannerComponent,
        UpdateModalComponent,
        AppDownloadBannerComponent,
        RateLimitBannerComponent,
    ],
    templateUrl: './app.component.html',
    styleUrl: './app.component.scss'
})

export class AppComponent implements OnInit, OnDestroy {
  private downloadBannerDismissed = signal(false);

  // True when either the update banner or the download banner is currently shown
  bannerVisible = computed(() => {
    const updateBannerVisible = this.releaseConfigService.versionStatus() === 'deprecated'
        && !this.releaseConfigService.isDismissed();
    if (updateBannerVisible) return true;
    if (this.downloadBannerDismissed()) return false;
    return this.isDownloadBannerVisible();
  });

  onDownloadBannerDismissed(): void {
    this.downloadBannerDismissed.set(true);
  }

  private isDownloadBannerVisible(): boolean {
    const storedDate = localStorage.getItem('appBannerDate');
    const today = new Date().toISOString().slice(0, 10);
    if (storedDate === today) return false;
    return !!this.detectBannerPlatform();
  }

  private detectBannerPlatform(): string | null {
    const ua = navigator.userAgent;
    if (/android/i.test(ua)) return 'android';
    if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
    if (/windows nt/i.test(ua)) return 'windows';
    if (/macintosh|mac os x/i.test(ua) && !/chrome/i.test(ua)) return 'macos';
    return null;
  }

  private readonly destroyRef = inject(DestroyRef);

  constructor(
    public cc: CacheService,
    public ss: SettingsService,
    public as: AnalyticsService,
    public router: Router,
    public activeRoute: ActivatedRoute,
    private route: ActivatedRoute,
    private storageService: StorageService,
    public authService: AuthService,
    private attachmentQueueService: AttachmentQueueService,
    private entityRemovalNotificationProcessor: EntityRemovalNotificationProcessorService,
    private logShipperService: LogShipperService,
    private habitTimerSync: HabitTimerSyncService, // Inject to initialize timer sync
    private releaseConfigService: ReleaseConfigService,
  ) {
    // Start the EntityRemovalNotificationProcessor to handle entity removal notifications
    this.entityRemovalNotificationProcessor.start();

    // Initialize log shipping to Cloud Logging (production only)
    this.logShipperService.init();

    window.addEventListener('beforeunload', (event: BeforeUnloadEvent) => {
      // Warn user if there are pending uploads before closing browser/tab
      if (this.attachmentQueueService.hasPendingUploads()) {
        // Standard way to trigger browser's "Leave site?" confirmation dialog
        // Note: modern browsers ignore the custom message and show their own generic text,
        // but we use the translation key for consistency and in case browser behavior changes.
        event.preventDefault();
        event.returnValue = this.ss.t('pendingUploads_browserCloseWarning');
      }
      this.as.appExitEvent(this.cc.user.uid || '');
    });

  }

  ngOnInit() {
    this.analyticsInit();
  };

  analyticsInit() {
    this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      ).subscribe(() => {

        let route = this.activeRoute;
        while (route.firstChild) {
          route = route.firstChild;
        }
        const screen = route.snapshot.data['screen'];
        this.as.currentScreen.set(screen || 'other');

        const queryParams = this.route.snapshot.queryParams;

        let queryUtmSource = queryParams['utm_source'];
        let queryUtmCampaign = queryParams['utm_campaign'];
        let queryUtmMedium = queryParams['utm_medium'];
        let queryUtmContent = queryParams['utm_content'];
        let queryDesignerId = queryParams['d_id'];
        let queryReferrerId = queryParams['r_id'];
        const muid = queryParams['m_uid'];
        const landingPage = queryParams['landing_page'];

        const firstUtmSource = queryParams['first_utm_source'];
        const firstUtmCampaign = queryParams['first_utm_campaign'];
        const firstUtmMedium = queryParams['first_utm_medium'];
        const firstUtmContent = queryParams['first_utm_content'];
        const firstDesignerId = queryParams['first_d_id'];
        const firstReferrerId = queryParams['first_r_id'];

        let lastUtmSource = queryParams['last_utm_source'];
        let lastUtmCampaign = queryParams['last_utm_campaign'];
        let lastUtmMedium = queryParams['last_utm_medium'];
        let lastUtmContent = queryParams['last_utm_content'];
        let lastDesignerId = queryParams['last_d_id'];
        let lastReferrerId = queryParams['last_r_id'];

        const muidFromStorage = this.storageService.getMuid();
        if (!muidFromStorage && muid) {
          this.storageService.setMuid(muid);
          this.as.commonProperties.muid = muid;
        }

        if (landingPage) {
          this.storageService.setWebLandingPage(landingPage);
          this.as.initialParams.initial_landing_page = landingPage;
        }

        // UTM source

        if (!queryUtmSource && firstUtmSource) {
          queryUtmSource = firstUtmSource;
        }

        if (!lastUtmSource && queryUtmSource) {
          lastUtmSource = queryUtmSource;
        }

        if (lastUtmSource) {
          this.storageService.setLastUtmSource(lastUtmSource);
        }
        const utmSource = this.storageService.getUtmSource();
        if (queryUtmSource && !utmSource) {
          this.as.initialParams.initial_utm_source = queryUtmSource;
          this.storageService.setUtmSource(queryUtmSource);
        } else {
          this.as.initialParams.initial_utm_source = utmSource || queryUtmSource || 'none';
        }

        // UTM campaign
        if (!queryUtmCampaign && firstUtmCampaign) {
          queryUtmCampaign = firstUtmCampaign;
        }
        if (!lastUtmCampaign && queryUtmCampaign) {
          lastUtmCampaign = queryUtmCampaign;
        }
        if (lastUtmCampaign) {
          this.storageService.setLastUtmCampaign(lastUtmCampaign);
        }
        const utmCampaign = this.storageService.getUtmCampaign();
        if (queryUtmCampaign && !utmCampaign) {
          this.as.initialParams.initial_utm_campaign = queryUtmCampaign;
          this.storageService.setUtmCampaign(queryUtmCampaign);
        } else {
          this.as.initialParams.initial_utm_campaign = utmCampaign || queryUtmCampaign || 'none';
        }

        // UTM medium
        if (!queryUtmMedium && firstUtmMedium) {
          queryUtmMedium = firstUtmMedium;
        }
        if (!lastUtmMedium && queryUtmMedium) {
          lastUtmMedium = queryUtmMedium;
        }
        if (lastUtmMedium) {
          this.storageService.setLastUtmMedium(lastUtmMedium);
        }
        const utmMedium = this.storageService.getUtmMedium();
        if (!utmMedium) {
          this.as.initialParams.initial_utm_medium = queryUtmMedium || 'direct';
          this.storageService.setUtmMedium(queryUtmMedium || 'direct');
        } else {
          this.as.initialParams.initial_utm_medium = utmMedium || queryUtmMedium || 'direct';
        }

        // UTM content

        if (!queryUtmContent && firstUtmContent) {
          queryUtmContent = firstUtmContent;
        }
        if (!lastUtmContent && queryUtmContent) {
          lastUtmContent = queryUtmContent;
        }
        if (lastUtmContent) {
          this.storageService.setLastUtmContent(lastUtmContent);
        }
        const utmContent = this.storageService.getUtmContent();
        if (queryUtmContent && !utmContent) {
          this.as.initialParams.initial_utm_content = queryUtmContent;
          this.storageService.setUtmContent(queryUtmContent);
        } else {
          this.as.initialParams.initial_utm_content = utmContent || queryUtmContent || 'none';
        }

        // Designer id

        if (!queryDesignerId && firstDesignerId) {
          queryDesignerId = firstDesignerId;
        }
        if (!lastDesignerId && queryDesignerId) {
          lastDesignerId = queryDesignerId;
        }
        if (lastDesignerId) {
          this.storageService.setLastDesignerId(lastDesignerId);
        }
        const designerId = this.storageService.getDesignerId();
        if (queryDesignerId && !designerId) {
          this.as.initialParams.initial_d_id = queryDesignerId;
          this.storageService.setDesignerId(queryDesignerId);
        } else {
          this.as.initialParams.initial_d_id = designerId || queryDesignerId || 'none';
        }

        // Referrer id

        if (!queryReferrerId && firstReferrerId) {
          queryReferrerId = firstReferrerId;
        }
        if (!lastReferrerId && queryReferrerId) {
          lastReferrerId = queryReferrerId;
        }
        if (lastReferrerId) {
          this.storageService.setLastReferrerId(lastReferrerId);
        }
        const referrerId = this.storageService.getReferrerId();
        if (queryReferrerId && !referrerId) {
          this.as.initialParams.initial_r_id = queryReferrerId;
          this.storageService.setReferrerId(queryReferrerId);
        } else {
          this.as.initialParams.initial_r_id = referrerId || queryReferrerId || 'none';
        }

      });
      this.as.init();
  }

  ngOnDestroy() {
    this.as.destroy();
  }
}
